import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Axios from 'axios';

import { Grid } from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';
import { useConfig } from 'src/hooks/useConfig';
import { axiosHeaders, getLocalisedErrorString, appendContactSupport } from 'src/utils/helpers';
import authService from 'src/utils/authService';
import SideBarLayout from 'src/shared/layouts/SideBarLayout';
import WorkflowTopBar from 'src/workflows/components/WorkflowTopBar/WorkflowTopBar';
import WorkflowsEmptyContainer from 'src/workflows/components/WorkflowsEmptyContainer/WorkflowsEmptyContainer';
import CreateProject from 'src/workflows/components/CreateProject/CreateProject';
import AddNewWorkflow from 'src/workflows/components/AddNewWorkflow/AddNewWorkflow';
import EditProjectWorkflow from 'src/workflows/components/EditProjectWorkflow/EditProjectWorkflow';
import WorkflowsTable from 'src/workflows/components/WorkflowsTable/WorkflowsTable';
import ManageWorkflow from 'src/workflows/components/ManageWorkflow/ManageWorkflow';
import TablePagination from 'src/shared/components/TablePagination';

const Workflows = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { workflowId } = useParams();
  const { BRAND_NAME, ENVIRONMENT, API, LOGIN_PANEL_URL } = useConfig();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [workflows, setWorkflows] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Record<string, any> | null>(null);
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [createWorkflowOpen, setCreateWorkflowOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEditItem, setSelectedEditItem] = useState<any>(null);
  const [manageWorkflowOpen, setManageWorkflowOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (user?.userRole !== 'admin' || ENVIRONMENT === 'prod') {
      navigate('/');
    }
  }, []);



  const fetchWorkflowsData = async (pageNumber = page, pageLimit = limit) => {
    if (!authService.validateToken()) {
      enqueueSnackbar(t('PROCYS_LOGIN_SESSION_EXPIRED'), {
        variant: 'error',
      });
      setTimeout(() => {
        authService.logout(LOGIN_PANEL_URL);
      }, 2000);
      return;
    }

    setLoading(true);
    try {
      const response = await Axios.get(
        `${API.workflowsProjects}/${encodeURIComponent(user?.companyID || '')}?page=${pageNumber + 1}&limit=${pageLimit}`,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );

      if (response.data.success && response.data.data) {
        const transformedData = response.data.data.map((item: any) => ({
          id: item.projectId,
          workflow: item.workflowId || item.projectId,
          name: item.projectName,
          description: item.projectDescription || '',
          documentsProcessed: 0,
          assignedUser: item.users?.length > 0 ? item.users.map((user: any) => user.email).join(', ') : '',
          users: item.users?.map((user: any) => user.email) || [],
          isDefault: false,
          workflowName: item.workflowName,
          workflowDescription: item.workflowDescription,
          isWorkflowActive: item.isWorkflowActive
        })) || [];

        setWorkflows(transformedData);
        setTotal(response.data.total || transformedData.length);
      } else {
        setWorkflows([]);
        setTotal(0);
      }
    } catch (error: any) {
      enqueueSnackbar(
        appendContactSupport(
          window.config.support_email,
          getLocalisedErrorString(error?.response?.data?.i18n || 'WORKFLOWS_FETCH_ERROR', t),
          t
        ),
        {
          variant: 'error',
        }
      );
      setWorkflows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const updateWorkflowData = useCallback(() => {
    fetchWorkflowsData(page, limit);
  }, [user?.companyID, page, limit]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchWorkflowsData(newPage, limit);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(0);
    fetchWorkflowsData(0, newLimit);
  };

  useEffect(() => {
    updateWorkflowData();
  }, []);

  useEffect(() => {
    if (workflowId && workflows.length > 0) {
      const workflow = workflows.find(w =>
        w.workflow === workflowId
        ||
        w.workflow === parseInt(workflowId, 10)
      );
      if (workflow) {
        setSelectedWorkflow(workflow);
        setManageWorkflowOpen(true);
      }
    } else if (workflowId === undefined) {
      setSelectedWorkflow(null);
      setManageWorkflowOpen(false);
    }
  }, [workflowId, workflows]);

  const handleCreateProjectClose = () => {
    setCreateProjectOpen(false);
  };

  const handleProjectCreated = () => {
    setPage(0);
    fetchWorkflowsData(0, limit);
  };

  const handleCreateWorkflowClose = () => {
    setCreateWorkflowOpen(false);
  };

  const handleWorkflowCreated = () => {
    setPage(0);
    fetchWorkflowsData(0, limit);
  };

  const onEdit = (workflow: Record<string, any>) => {
    const hasWorkflow = workflow.workflowName && workflow.workflow !== workflow.id;

    const editData = {
      type: hasWorkflow ? 'workflow' : 'project',
      projectId: workflow.id,
      workflowId: hasWorkflow ? workflow.workflow : undefined,
      projectName: workflow.name,
      projectDescription: workflow.description,
      workflowName: hasWorkflow ? workflow.workflowName : undefined,
      workflowDescription: hasWorkflow ? workflow.workflowDescription : undefined,
      workflowUsers: hasWorkflow ? workflow.users : undefined,
    };

    setSelectedEditItem(editData);
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedEditItem(null);
  };

  const handleProjectUpdated = () => {
    setPage(0);
    fetchWorkflowsData(0, limit);
  };

  const handleWorkflowUpdated = () => {
    setPage(0);
    fetchWorkflowsData(0, limit);
  };

  const onManageFields = async (workflow: Record<string, any>) => {
    setSelectedWorkflow(workflow);
    setManageWorkflowOpen(true);
    const workflowIdentifier = workflow.workflow || workflow.id;
    navigate(`/workflows/${workflowIdentifier}`);
  };

  const handleManageWorkflowClose = () => {
    setSelectedWorkflow(null);
    setManageWorkflowOpen(false);
    if (workflowId) {
      navigate('/workflows');
    }
  };

  return (
    <SideBarLayout title={`${t('WORKFLOW_TITLE')} | ${BRAND_NAME}`}>
      {manageWorkflowOpen ? (
        <ManageWorkflow
          selectedWorkflow={selectedWorkflow}
          handleClose={handleManageWorkflowClose}
        />
      ) : (
        <Grid sx={styles.root}>
          <WorkflowTopBar
            loading={loading}
            workflows={workflows}
            setAddNewOpen={() => setCreateProjectOpen(true)}
            setCreateWorkflowOpen={() => setCreateWorkflowOpen(true)}
          />
          <Grid sx={styles.container} className="x-scroll">
            {workflows.length > 0 ? (
              <>
                <WorkflowsTable
                  loading={loading}
                  workflows={workflows}
                  onEdit={onEdit}
                  onManageFields={onManageFields}
                />
                {!loading && <Grid sx={{ display: 'flex', flex: 1 }} />}
                <TablePagination
                  total={total}
                  limit={limit}
                  page={page}
                  onLimitChange={handleLimitChange}
                  onPageChange={handlePageChange}
                />
              </>
            ) : (
              <WorkflowsEmptyContainer
                loading={loading}
                handleAddNewOpen={() => setCreateProjectOpen(true)}
              />
            )}
          </Grid>
        </Grid>
      )}
      <CreateProject
        open={createProjectOpen}
        handleClose={handleCreateProjectClose}
        onProjectCreated={handleProjectCreated}
      />
      <AddNewWorkflow
        open={createWorkflowOpen}
        handleClose={handleCreateWorkflowClose}
        onWorkflowCreated={handleWorkflowCreated}
      />
      <EditProjectWorkflow
        open={editDialogOpen}
        handleClose={handleEditDialogClose}
        selectedItem={selectedEditItem}
        onProjectUpdated={handleProjectUpdated}
        onWorkflowUpdated={handleWorkflowUpdated}
      />
    </SideBarLayout>
  );
}

export default Workflows;

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    backgroundColor: '#F3F5F9',
    minHeight: '90%'
  },
  container: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    width: '100%',
    backgroundColor: '#FFFFFF',
    overflow: 'auto',
    padding: '0px',
    height: '100%',
    borderBottomLeftRadius: '10px',
    borderBottomRightRadius: '10px',
    boxShadow: '0px 10px 10px 0px #4C4E6438',
  },
}
